import React, { useMemo, useState } from 'react';
import { sum } from 'lodash';

import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import MuiFilterIcon from '@material-ui/icons/FilterList';
import MoreIcon from '@material-ui/icons/MoreVert';

import ExportIcon from '../../assets/icons/DownloadBrowserIcon';
import LabelIcon from '../../assets/icons/LabelIcon';
import EditIcon from '../../assets/icons/EditIcon';
import AddIcon from '../../assets/icons/AddIcon';
import PipelineIcon from '../../assets/icons/PipelineIcon';
import DeleteIcon from '../../assets/icons/DeleteIcon';
import FilterIcon from '../../assets/icons/FilterIcon';
import SpecialityIcon from '../../assets/icons/SpecialityIcon';

import ActionIconButton from '../shared/ActionIconButton';
import Select from '../shared/Select';
import Button from '../shared/Button';
import ColorButton from '../shared/ColorButton';

import { formatPrice } from '../../utils/utils';

import userRoles from '../../constants/userRoles';

import './sales.scss';
import StarCircleIcon from '../../assets/icons/StarCircleIcon';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    paddingTop: '12px',
    paddingBottom: '12px',
    color: 'var(--app-text)',
  },
}))(MenuItem);

const SalesPageActions = (props) => {
  const {
    userRole,
    pipelines = [],
    stages = [],
    leads = [],
    selectedPipeline,
    onSelectPipeline,
    editMode,
    onCreateClick,
    onEditClick,
    onRenameClick,
    onDeleteClick,
    onSaveChangesClick,
    onEditLabelsClick,
    onEditSpecialitiesClick,
    onAddLeadClick,
    onCancelClick,
    onEditRankingClick,
    loading,
    exportLoading,
    onExportClick,
    onFilterClick,
    activeFilterCount = 0,
  } = props;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const totalValue = useMemo(() => sum(
    stages.map((stage) => (
      stage.probability ? (
        sum(leads?.filter((l) => l.stage === stage.id).map((l) => l.anticipatedValue || 0)) * (stage.probability / 100) || 0
      ) : 0
    ))
  ), [stages, leads]);

  return (
    <div className='sales-page__actions'>
      {!!selectedPipeline ? (
        editMode ? (
          <div className='d-flex'>
            <ColorButton
              onClick={onSaveChangesClick}
              color='blue'
              text="Save Changes"
              fontSize={14}
              height={60}
              disabled={loading}
              loading={loading}
            />
            <div className='m-l-md'>
              <ColorButton
                onClick={onCancelClick}
                color='grey'
                text="Cancel"
                fontSize={14}
                height={60}
              />
            </div>
          </div>
        ) : (
          <ActionIconButton
            onClick={onAddLeadClick}
            color='blue'
            tooltip="Add Lead"
            size='large'
          >
            <AddIcon />
          </ActionIconButton>
        )
      ) : (
        <div />
      )}

      <div className="d-flex">
        <div className='sales-page__leads-info'>
          <div className='d-flex m-r-lg'>
            <FilterIcon color="var(--app-text)" />
            <div className='font-bold m-l-md'>Leads</div>
          </div>
          <div className='d-flex'>
            {`${leads.length} | $${formatPrice(totalValue, 0)}`}
          </div>
        </div>

        <Select
          endAdornment="Select Pipeline"
          value={selectedPipeline}
          onChange={onSelectPipeline}
          options={pipelines?.map((pipeline) => ({
            key: pipeline.id,
            value: pipeline.id,
            label: pipeline.title,
            icon: <PipelineIcon color="var(--app-text)" />
          }))}
        />

        <div className="m-l-md sales-page__filter-button">
          <Button
            type="icon"
            onClick={onFilterClick}
          >
            <MuiFilterIcon style={{ color: 'var(--app-text)' }} />
          </Button>
          {!!activeFilterCount && (
            <div className='sales-page__filter-count'>{activeFilterCount}</div>
          )}
        </div>

        {userRole === userRoles.ADMIN.name && (
          <>
            <div className="m-l-md">
              <Button
                type="icon"
                aria-label="more"
                aria-controls="long-menu"
                aria-haspopup="true"
                loading={exportLoading}
                disabled={exportLoading}
                onClick={handleClick}
              >
                <MoreIcon style={{ color: 'var(--app-text)' }} />
              </Button>
            </div>

            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleClose}
              PaperProps={{
                style: {
                  // maxHeight: ITEM_HEIGHT * 4.5,
                  width: 200,
                },
                elevation: 3,
              }}
            >
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  onExportClick();
                }}
                disabled={!selectedPipeline}
              >
                <ExportIcon color="var(--app-text)" />
                <span className="m-l-md">Export All</span>
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  onEditLabelsClick();
                }}
              >
                <LabelIcon color="var(--app-text)" />
                <span className="m-l-md">Edit Labels</span>
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  onEditSpecialitiesClick();
                }}
              >
                <SpecialityIcon color="var(--app-text)" />
                <span className="m-l-md">Edit Specialities</span>
              </StyledMenuItem>
              <StyledMenuItem
                onClick={() => {
                  handleClose();
                  onEditRankingClick();
                }}
              >
                <StarCircleIcon color="var(--app-text)" />
                <span className="m-l-md">Edit Rank & Activity</span>
              </StyledMenuItem>
              <StyledMenuItem
                disabled={!selectedPipeline}
                onClick={() => {
                  handleClose();
                  onEditClick();
                }}
              >
                <EditIcon color="var(--app-text)" />
                <span className="m-l-md">Edit Pipeline</span>
              </StyledMenuItem>

              {!!selectedPipeline && (
                <StyledMenuItem
                  disabled={!selectedPipeline}
                  onClick={() => {
                    handleClose();
                    onRenameClick();
                  }}
                >
                  <EditIcon color="var(--app-text)" />
                  <span className="m-l-md">Rename Pipeline</span>
                </StyledMenuItem>
              )}

              <StyledMenuItem
                disabled={!selectedPipeline}
                onClick={() => {
                  handleClose();
                  onDeleteClick();
                }}
              >
                <DeleteIcon color="#F53434" />
                <span className="m-l-md">Delete Pipeline</span>
              </StyledMenuItem>

              <div className='sales-page__add-new-pipeline-button'>
                <ColorButton
                  color='blue'
                  text='Add New Pipeline'
                  onClick={() => {
                    handleClose();
                    onCreateClick();
                  }}
                />
              </div>
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default SalesPageActions;
